import {Injectable} from '@angular/core';
import {AbstractFirestoreService} from '@shared/services/abstracts/AbstractFirestoreService';
import {AuthService} from '@shared/services/auth.service';
import type {PropertyVatProfile} from '@vat/engine/types';
import {firstValueFrom, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {
    buildDefaultPropertyVatProfile,
    buildDismissedHintUpdate,
    buildInputTaxKeyUpdate,
    buildVacancyIntentUpdate,
    InputTaxKey,
    resolvePropertyVatProfile
} from './property-vat-profile.util';

/**
 * Firestore-Zugriff auf `vatPropertyProfiles/{propertyID}` (Spec 3.3). Bewusst nicht am Property,
 * weil jedes Property-Update Algolia/Perioden-Sync auslöst und create-expense das Property snapshotet.
 * Verschachtelte Felder werden nur per dotted key mit `update()` geschrieben – deshalb legt `ensure()`
 * das Dokument vorher mit Default AUTO_FLAECHE an.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable({providedIn: 'root'})
export class VatPropertyProfilesService extends AbstractFirestoreService<PropertyVatProfile> {

    COLLECTION = 'vatPropertyProfiles';

    constructor(authService: AuthService) {
        super(authService);
    }

    /** Ohne Dokument gilt AUTO_FLAECHE (Spec 3.3) – Ansehen schreibt nichts. */
    profile$(propertyID: string): Observable<PropertyVatProfile> {
        return this.doc(propertyID).valueChanges().pipe(
            map(doc => resolvePropertyVatProfile(doc, {propertyID, customerID: this.authService.customerID ?? null}))
        );
    }

    async updateInputTaxKey(propertyID: string, key: InputTaxKey, year: number | null): Promise<void> {
        await this.ensure(propertyID);
        return this.doc(propertyID).update(buildInputTaxKeyUpdate(key, year, this.uid(), new Date()) as Partial<PropertyVatProfile>);
    }

    async setVacancyIntent(propertyID: string, unitID: string, intent: 'STEUERPFLICHTIG' | 'STEUERFREI'): Promise<void> {
        await this.ensure(propertyID);
        return this.doc(propertyID).update(buildVacancyIntentUpdate(unitID, intent, this.uid(), new Date()) as Partial<PropertyVatProfile>);
    }

    async dismissHint(propertyID: string, periodID: string, hintID: string): Promise<void> {
        await this.ensure(propertyID);
        return this.doc(propertyID).update(buildDismissedHintUpdate(periodID, hintID, this.uid(), new Date()) as Partial<PropertyVatProfile>);
    }

    /** Legt das Dokument mit Default AUTO_FLAECHE an, falls es fehlt – danach sind dotted-key-Updates möglich. */
    private async ensure(propertyID: string): Promise<void> {
        const snapshot = await firstValueFrom(this.doc(propertyID).get({source: 'server'}));
        if (!snapshot.exists) {
            await this.doc(propertyID).set(buildDefaultPropertyVatProfile({
                propertyID,
                customerID: this.authService.customerID ?? null,
                uid: this.uid(),
                now: new Date(),
            }));
        }
    }

    private uid(): string {
        return this.authService.authUser?.uid ?? '';
    }
}
